import React, { useState } from 'react';
import { Loader2, Layers, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';
import { formatCurrency } from '../../lib/utils';

const STATUS_COLORS = {
  fully_invoiced: 'bg-green-100 text-green-800',
  partially_invoiced: 'bg-amber-100 text-amber-800',
  not_invoiced: 'bg-red-100 text-red-800',
  over_invoiced: 'bg-purple-100 text-purple-800',
  on_schedule: 'bg-blue-100 text-blue-800',
};

const STATUS_LABELS = {
  fully_invoiced: 'Fully invoiced',
  partially_invoiced: 'Partially invoiced',
  not_invoiced: 'Not invoiced',
  over_invoiced: 'Over-invoiced',
  on_schedule: 'Billed up to date',
};

function StatusChip({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-800'}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function fmt(v) {
  if (v == null) return 'n/a';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (isNaN(n)) return String(v);
  return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function GroupRow({ group, dc }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr
        className={`border-b cursor-pointer hover:bg-muted/50 ${group.is_multi_segment ? 'font-medium' : ''}`}
        onClick={() => setOpen(!open)}
      >
        <td className="py-2 pr-2">
          <span className="inline-flex items-center gap-1">
            {group.is_multi_segment && (open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />)}
            <span className="font-mono">{group.root_id}</span>
          </span>
        </td>
        <td className="py-2 pr-2 text-center">
          {group.is_multi_segment && (
            <Badge variant="outline" className="text-xs">{group.segment_count} segs</Badge>
          )}
        </td>
        <td className="py-2 pr-2 text-right font-mono">{fmt(group.group_closed)}</td>
        <td className="py-2 pr-2 text-right font-mono">{fmt(group.group_invoiced)}</td>
        <td className="py-2 pr-2 text-right font-mono">{fmt(group.group_delta)}</td>
        <td className="py-2 pr-2 text-right">{group.coverage_pct}%</td>
        <td className="py-2 pr-2"><StatusChip status={group.group_status} /></td>
        <td className="py-2 pr-2 text-xs text-muted-foreground">
          {group.matched_invoice_ids?.join(', ') || '-'}
        </td>
      </tr>
      {open && group.segments?.map((seg) => (
        <React.Fragment key={seg.booking_id}>
          <tr className="border-b bg-muted/30 text-xs">
            <td className="py-1 pr-2 pl-6 font-mono">
              {seg.booking_id}
              {seg.client_pool_fallback && (
                <Badge variant="destructive" className="text-[9px] ml-1">pool</Badge>
              )}
            </td>
            <td className="py-1 pr-2 text-center text-muted-foreground">{seg.client}</td>
            <td className="py-1 pr-2 text-right font-mono">{fmt(seg.booking_value)}</td>
            <td className="py-1 pr-2 text-right font-mono">{fmt(seg.invoiced_total)}</td>
            <td className="py-1 pr-2 text-right font-mono">{fmt(seg.delta)}</td>
            <td className="py-1 pr-2 text-[9px] text-muted-foreground">{seg.vat_basis || '-'}</td>
            <td className="py-1 pr-2"><StatusChip status={seg.recon_status} /></td>
            <td className="py-1 pr-2 text-muted-foreground">
              {seg.reasons?.join(', ') || '-'}
            </td>
          </tr>
          {seg.linked_invoices?.length > 0 && (
            <tr className="border-b bg-muted/10 text-[10px]">
              <td colSpan={8} className="py-1 pl-10 pr-2 text-muted-foreground">
                {seg.linked_invoices.map((inv) => (
                  <span key={inv.invoice_id} className={`inline-block mr-3 ${inv.voided ? 'line-through opacity-50' : ''}`}>
                    {inv.invoice_id}: {fmt(inv.amount)} {inv.currency}
                    {inv.voided && ' (voided)'}
                  </span>
                ))}
              </td>
            </tr>
          )}
        </React.Fragment>
      ))}
    </>
  );
}

export function BookingReconcileGroupingPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.bookingReconcileGrouping({ month, currency: 'eur' });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const r = result;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Layers className="h-5 w-5 text-blue-600" />
          Booking Reconcile Grouping
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Shows how sibling segments are grouped under their root enquiry ID for consolidated-invoice
          attribution. Multi-segment groups appear first.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Month (YYYY-MM)</label>
            <input
              type="text"
              className="border rounded px-2 py-1 text-sm w-28"
              placeholder="2026-06"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          </div>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Run Diagnostic
          </Button>
        </div>

        {r && (
          <div className="space-y-3">
            <div className="flex gap-4 text-sm">
              <Badge variant="outline">{r.total_groups} groups</Badge>
              <Badge variant={r.multi_segment_groups > 0 ? 'default' : 'secondary'}>
                {r.multi_segment_groups} multi-segment
              </Badge>
              <Badge variant="outline">{r.total_segments} total segments</Badge>
            </div>

            {r.groups?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="text-xs w-full">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-1 pr-2">Root / Segment</th>
                      <th className="py-1 pr-2 text-center">Segs</th>
                      <th className="py-1 pr-2 text-right">Closed</th>
                      <th className="py-1 pr-2 text-right">Invoiced</th>
                      <th className="py-1 pr-2 text-right">Delta</th>
                      <th className="py-1 pr-2 text-right">Coverage</th>
                      <th className="py-1 pr-2">Status</th>
                      <th className="py-1 pr-2">Invoices</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.groups.map((g) => (
                      <GroupRow key={g.root_id} group={g} dc={r.display_currency} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {r.groups?.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No bookings found for {r.month}.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
